import { useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Code } from 'lucide-react';

interface EmailHtmlEditorProps {
  value: string;
  onChange: (html: string) => void;
}

// Toolbar do WYSIWYG — mesmo conjunto usado em ContractTemplateEditor.
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: [] }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};

export default function EmailHtmlEditor({ value, onChange }: EmailHtmlEditorProps) {
  const quillRef = useRef<ReactQuill>(null);
  const [showHtml, setShowHtml] = useState(false);
  const [varName, setVarName] = useState('');

  // Insere {{nome}} na posição do cursor do Quill (fallback: ao final do conteúdo).
  const inserirVariavel = () => {
    const nome = varName.trim();
    if (!nome) return;
    const token = `{{${nome}}}`;
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      editor.insertText(index, token, 'user');
      editor.setSelection(index + token.length, 0);
      onChange(editor.root.innerHTML);
    } else {
      onChange(`${value}${token}`);
    }
    setVarName('');
  };

  return (
    <div className="space-y-2">
      <div className="border border-gray-200 rounded-lg overflow-hidden [&_.quill]:border-0 [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:rounded-b-lg [&_.ql-editor]:min-h-[220px]">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={QUILL_MODULES}
          placeholder="<p>Olá {{nome}}...</p>"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setShowHtml((s) => !s)}>
          <Code className="w-4 h-4 mr-1" />
          {showHtml ? 'Esconder HTML' : 'Visualizar HTML'}
        </Button>
        <div className="flex items-center gap-1">
          <Input
            value={varName}
            onChange={(e) => setVarName(e.target.value)}
            placeholder="nome da variável"
            className="h-8 w-[180px]"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); inserirVariavel(); } }}
          />
          <Button type="button" variant="outline" size="sm" onClick={inserirVariavel} disabled={!varName.trim()}>
            Inserir variável
          </Button>
        </div>
      </div>

      {showHtml && (
        <div>
          <Label htmlFor="email-html-source" className="text-sm">Código HTML</Label>
          <textarea
            id="email-html-source"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={12}
            className="w-full font-mono text-xs border rounded p-2"
            placeholder="<p>Olá {{nome}}...</p>"
          />
        </div>
      )}
    </div>
  );
}
